import { create } from 'zustand'

export type AppView =
  | 'landing'
  | 'login'
  | 'register'
  | 'forgot-password'
  | 'module-selection'
  | 'dashboard'
  | 'products'
  | 'pricing'
  | 'solutions'
  | 'partners'
  | 'company'
  | 'careers'
  | 'blog'
  | 'contact'
  | 'community'
  | 'support'
  | 'resources'
  | 'terms'
  | 'privacy'
  | 'cpanel'

export type DashboardTab =
  | 'overview'
  | 'crm'
  | 'tasks'
  | 'hrm'
  | 'inventory'
  | 'expenses'
  | 'assets'
  | 'messaging'
  | 'visitors'
  | 'documents'
  | 'todo'
  | 'sticky-notes'
  | 'law-firm'
  | 'settings'
  | 'profile'
  | 'users'
  | 'roles'
  | 'appearance'
  | 'procurement'
  | 'projects'
  | 'sales'
  | 'billing'
  | 'forms'
  | 'security-center'
  | 'quickdrop'
  | 'vendor-management'
  | 'contacts'
  | 'workspace'

export type CRMSubTab =
  | 'dashboard'
  | 'contacts'
  | 'leads'
  | 'deals'
  | 'activities'
  | 'quotations'
  | 'reports'
  | 'settings'

export type HRMSubTab =
  | 'dashboard'
  | 'employees'
  | 'attendance'
  | 'leave'
  | 'payroll'
  | 'recruitment'
  | 'recruitment-vacancies'
  | 'recruitment-advertisements'
  | 'recruitment-candidates'
  | 'recruitment-interviews'
  | 'recruitment-call-letters'
  | 'recruitment-assessments'
  | 'recruitment-offers'
  | 'performance'
  | 'loans'
  | 'reports'
  | 'documents'
  | 'reminders'
  | 'settings'
  // Employee Amenities
  | 'amenities-sim-cards'
  | 'amenities-mobiles'
  | 'amenities-quarters'
  | 'amenities-accommodation'
  | 'amenities-vehicles'
  | 'amenities-fuel-cards'
  | 'amenities-inventory'
  | 'amenities-maintenance'
  | 'amenities-requests'
  | 'amenities-approvals'
  | 'amenities-returns'
  | 'amenities-incidents'
  | 'amenities-vendors'
  | 'amenities-analytics'

export type InventorySubTab =
  | 'dashboard'
  | 'products'
  | 'movements'
  | 'reports'
  | 'settings'

export type AssetsSubTab =
  | 'dashboard'
  | 'assets'
  | 'maintenance'
  | 'reports'
  | 'reports-print'
  | 'settings'

export type ProcurementSubTab =
  | 'dashboard'
  | 'requests'
  | 'suppliers'
  | 'purchase-orders'
  | 'goods-receipts'
  | 'reports'
  | 'settings'

export type ProjectsSubTab =
  | 'dashboard'
  | 'projects'
  | 'tasks'
  | 'milestones'
  | 'reports'
  | 'settings'

export type SalesSubTab =
  | 'dashboard'
  | 'customers'
  | 'orders'
  | 'invoices'
  | 'payments'
  | 'reports'

export type BillingSubTab =
  | 'dashboard'
  | 'invoices'
  | 'quotations'
  | 'recurring'
  | 'payments'
  | 'reports'
  | 'templates'
  | 'portal'
  | 'credit-notes'
  | 'audit-logs'
  | 'notifications'
  | 'settings'

export type FormsSubTab =
  | 'dashboard'
  | 'builder'
  | 'submissions'
  | 'templates'
  | 'analytics'
  | 'automations'
  | 'settings'

export type ExpensesSubTab =
  | 'dashboard'
  | 'expenses'
  | 'approvals'
  | 'recurring'
  | 'budgets'
  | 'reports'
  | 'attachments'
  | 'settings'

export type VendorManagementSubTab =
  | 'dashboard'
  | 'directory'
  | 'registration'
  | 'contacts'
  | 'contracts'
  | 'compliance'
  | 'payments'
  | 'performance'
  | 'documents'
  | 'reports-print'
  | 'settings'

export type DocumentsSubTab =
  | 'dashboard'
  | 'documents'
  | 'folders'
  | 'shared'
  | 'reports'
  | 'settings'

export type PathologyLabSubTab =
  | 'dashboard'
  | 'patients'
  | 'doctors'
  | 'tests'
  | 'packages'
  | 'sample-collection'
  | 'sample-tracking'
  | 'work-queue'
  | 'result-entry'
  | 'result-verification'
  | 'reports'
  | 'report-designer'
  | 'barcode'
  | 'notifications'
  | 'billing'
  | 'payments'
  | 'quality-control'
  | 'equipment'
  | 'inventory'
  | 'analytics'
  | 'settings'

export type NexusHubSubTab =
  | 'dashboard'
  | 'communities'
  | 'discussions'
  | 'questions'
  | 'knowledge-base'
  | 'announcements'
  | 'members'
  | 'moderation'
  | 'analytics'
  | 'settings'

export type StickyNotesSubTab =
  | 'notes'
  | 'history'
  | 'preferences'

export type SettingsSubTab =
  | 'system'
  | 'configuration'
  | 'users'
  | 'roles'
  | 'appearance'
  | 'backup'
  | 'upgrade'

export type LawFirmSubTab =
  | 'dashboard'
  | 'cases'
  | 'clients'
  | 'hearings'
  | 'courts'
  | 'documents'
  | 'invoices'
  | 'tasks'
  | 'reports'
  | 'reports-print'

export type WorkspaceSubTab =
  | 'dashboard'
  | 'conversations'
  | 'documents'
  | 'workflow'
  | 'settings'

interface AppState {
  currentView: AppView
  setCurrentView: (view: AppView) => void
  activeTab: DashboardTab
  setActiveTab: (tab: DashboardTab) => void
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
  sidebarHoverMode: boolean
  setSidebarHoverMode: (mode: boolean) => void
  isDark: boolean
  toggleTheme: () => void
  isAuthenticated: boolean
  setIsAuthenticated: (auth: boolean) => void
  userId: string
  setUserId: (id: string) => void
  userName: string
  setUserName: (name: string) => void
  companyName: string
  setCompanyName: (name: string) => void
  tenantSlug: string
  setTenantSlug: (slug: string) => void
  userEmail: string
  setUserEmail: (email: string) => void
  userAvatarUrl: string
  setUserAvatarUrl: (url: string) => void
  userRole: string
  setUserRole: (role: string) => void
  currentLang: { code: string; name: string; flag: string }
  setCurrentLang: (lang: { code: string; name: string; flag: string }) => void
  isFullscreen: boolean
  setIsFullscreen: (fs: boolean) => void
  showWelcome: boolean
  setShowWelcome: (show: boolean) => void
  dontShowWelcomeAgain: boolean
  setDontShowWelcomeAgain: (v: boolean) => void
  showAppLauncher: boolean
  setShowAppLauncher: (show: boolean) => void
  showLogoutConfirm: boolean
  setShowLogoutConfirm: (show: boolean) => void
  crmSubTab: CRMSubTab
  setCRMSubTab: (tab: CRMSubTab) => void
  hrmSubTab: HRMSubTab
  setHRMSubTab: (tab: HRMSubTab) => void
  inventorySubTab: InventorySubTab
  setInventorySubTab: (tab: InventorySubTab) => void
  assetsSubTab: AssetsSubTab
  setAssetsSubTab: (tab: AssetsSubTab) => void
  procurementSubTab: ProcurementSubTab
  setProcurementSubTab: (tab: ProcurementSubTab) => void
  projectsSubTab: ProjectsSubTab
  setProjectsSubTab: (tab: ProjectsSubTab) => void
  salesSubTab: SalesSubTab
  setSalesSubTab: (tab: SalesSubTab) => void
  billingSubTab: BillingSubTab
  setBillingSubTab: (tab: BillingSubTab) => void
  expensesSubTab: ExpensesSubTab
  setExpensesSubTab: (tab: ExpensesSubTab) => void
  vendorManagementSubTab: VendorManagementSubTab
  setVendorManagementSubTab: (tab: VendorManagementSubTab) => void
  pathologyLabSubTab: PathologyLabSubTab
  setPathologyLabSubTab: (tab: PathologyLabSubTab) => void
  pathologyLabSettingsTab: string | null
  setPathologyLabSettingsTab: (tab: string | null) => void
  editingVendorId: string | null
  setEditingVendorId: (id: string | null) => void
  documentsSubTab: DocumentsSubTab
  setDocumentsSubTab: (tab: DocumentsSubTab) => void
  nexusHubSubTab: NexusHubSubTab
  setNexusHubSubTab: (tab: NexusHubSubTab) => void
  formsSubTab: FormsSubTab
  setFormsSubTab: (tab: FormsSubTab) => void
  settingsSubTab: SettingsSubTab
  setSettingsSubTab: (tab: SettingsSubTab) => void
  stickyNotesSubTab: StickyNotesSubTab
  setStickyNotesSubTab: (tab: StickyNotesSubTab) => void
  lawFirmSubTab: LawFirmSubTab
  setLawFirmSubTab: (tab: LawFirmSubTab) => void
  workspaceSubTab: WorkspaceSubTab
  setWorkspaceSubTab: (tab: WorkspaceSubTab) => void
  stickyNotesFloating: boolean
  setStickyNotesFloating: (floating: boolean) => void
  quickDropDocked: boolean
  setQuickDropDocked: (docked: boolean) => void
  stickyNotesDocked: boolean
  setStickyNotesDocked: (docked: boolean) => void
  dockedNoteIds: string[]
  setDockedNoteIds: (ids: string[]) => void
  noteGroups: Record<string, string[]>  // groupId -> noteIds
  setNoteGroups: (groups: Record<string, string[]>) => void
  searchOpen: boolean
  setSearchOpen: (open: boolean) => void
  notificationsOpen: boolean
  setNotificationsOpen: (open: boolean) => void
  selectedModules: string[]
  setSelectedModules: (modules: string[]) => void
  userPermissions: string[]
  setUserPermissions: (permissions: string[]) => void
  editingFormId: string | null
  setEditingFormId: (id: string | null) => void
  templateToLoad: { title: string; description: string; fields: any[] } | null
  setTemplateToLoad: (template: { title: string; description: string; fields: any[] } | null) => void
}

export const useAppStore = create<AppState>((set) => ({
  currentView: 'landing',
  setCurrentView: (view) => set({ currentView: view }),
  activeTab: 'overview',
  setActiveTab: (tab) => set({ activeTab: tab }),
  sidebarOpen: true,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  sidebarCollapsed: false,
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  sidebarHoverMode: true,
  setSidebarHoverMode: (mode) => set({ sidebarHoverMode: mode }),
  isDark: false,
  toggleTheme: () => set((state) => ({ isDark: !state.isDark })),
  isAuthenticated: false,
  setIsAuthenticated: (auth) => set({ isAuthenticated: auth }),
  userId: '',
  setUserId: (id) => set({ userId: id }),
  userName: 'Alex Johnson',
  setUserName: (name) => set({ userName: name }),
  companyName: 'ORYXOS Inc.',
  setCompanyName: (name) => set({ companyName: name }),
  tenantSlug: '',
  setTenantSlug: (slug) => set({ tenantSlug: slug }),
  userEmail: 'alex@oryxos.com',
  setUserEmail: (email) => set({ userEmail: email }),
  userAvatarUrl: '',
  setUserAvatarUrl: (url) => set({ userAvatarUrl: url }),
  userRole: 'Admin',
  setUserRole: (role) => set({ userRole: role }),
  currentLang: { code: 'us', name: 'English', flag: 'https://flagcdn.com/w20/us.png' },
  setCurrentLang: (lang) => set({ currentLang: lang }),
  isFullscreen: false,
  setIsFullscreen: (fs) => set({ isFullscreen: fs }),
  showWelcome: false,
  setShowWelcome: (show) => set({ showWelcome: show }),
  dontShowWelcomeAgain: false,
  setDontShowWelcomeAgain: (v) => set({ dontShowWelcomeAgain: v }),
  showAppLauncher: false,
  setShowAppLauncher: (show) => set({ showAppLauncher: show }),
  showLogoutConfirm: false,
  setShowLogoutConfirm: (show) => set({ showLogoutConfirm: show }),
  crmSubTab: 'dashboard',
  setCRMSubTab: (tab) => set({ crmSubTab: tab }),
  hrmSubTab: 'dashboard',
  setHRMSubTab: (tab) => set({ hrmSubTab: tab }),
  inventorySubTab: 'dashboard',
  setInventorySubTab: (tab) => set({ inventorySubTab: tab }),
  assetsSubTab: 'dashboard',
  setAssetsSubTab: (tab) => set({ assetsSubTab: tab }),
  procurementSubTab: 'dashboard',
  setProcurementSubTab: (tab) => set({ procurementSubTab: tab }),
  projectsSubTab: 'dashboard',
  setProjectsSubTab: (tab) => set({ projectsSubTab: tab }),
  salesSubTab: 'dashboard',
  setSalesSubTab: (tab) => set({ salesSubTab: tab }),
  billingSubTab: 'dashboard',
  setBillingSubTab: (tab) => set({ billingSubTab: tab }),
  expensesSubTab: 'dashboard',
  setExpensesSubTab: (tab) => set({ expensesSubTab: tab }),
  vendorManagementSubTab: 'dashboard',
  setVendorManagementSubTab: (tab) => set({ vendorManagementSubTab: tab }),
  pathologyLabSubTab: 'dashboard',
  setPathologyLabSubTab: (tab) => set({ pathologyLabSubTab: tab }),
  pathologyLabSettingsTab: null as string | null,
  setPathologyLabSettingsTab: (tab: string | null) => set({ pathologyLabSettingsTab: tab }),
  editingVendorId: null as string | null,
  setEditingVendorId: (id: string | null) => set({ editingVendorId: id }),
  documentsSubTab: 'dashboard',
  setDocumentsSubTab: (tab) => set({ documentsSubTab: tab }),
  nexusHubSubTab: 'dashboard',
  setNexusHubSubTab: (tab) => set({ nexusHubSubTab: tab }),
  formsSubTab: 'dashboard',
  setFormsSubTab: (tab) => set({ formsSubTab: tab }),
  settingsSubTab: 'system',
  setSettingsSubTab: (tab) => set({ settingsSubTab: tab }),
  stickyNotesSubTab: 'notes',
  setStickyNotesSubTab: (tab) => set({ stickyNotesSubTab: tab }),
  lawFirmSubTab: 'dashboard',
  setLawFirmSubTab: (tab) => set({ lawFirmSubTab: tab }),
  workspaceSubTab: 'dashboard',
  setWorkspaceSubTab: (tab) => set({ workspaceSubTab: tab }),
  stickyNotesFloating: true,
  setStickyNotesFloating: (floating) => set({ stickyNotesFloating: floating }),
  quickDropDocked: false,
  setQuickDropDocked: (docked) => set({ quickDropDocked: docked }),
  stickyNotesDocked: false,
  setStickyNotesDocked: (docked) => set({ stickyNotesDocked: docked }),
  dockedNoteIds: [],
  setDockedNoteIds: (ids) => set({ dockedNoteIds: ids }),
  noteGroups: {},
  setNoteGroups: (groups) => set({ noteGroups: groups }),
  searchOpen: false,
  setSearchOpen: (open) => set({ searchOpen: open }),
  notificationsOpen: false,
  setNotificationsOpen: (open) => set({ notificationsOpen: open }),
  selectedModules: ['CRM', 'Sales', 'HRM', 'Inventory', 'Expenses', 'Documents', 'Law Firm'],
  setSelectedModules: (modules) => set({ selectedModules: modules }),
  userPermissions: ['all'],
  setUserPermissions: (permissions) => set({ userPermissions: permissions }),
  editingFormId: null,
  setEditingFormId: (id) => set({ editingFormId: id }),
  templateToLoad: null,
  setTemplateToLoad: (template) => set({ templateToLoad: template }),
}))
